import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import { MapPin, Pencil, PowerOff } from 'lucide-react';
import MainLayout from '../../Components/Layout/MainLayout/MainLayout';
import { Button } from '../../Components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../Components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../../Components/ui/sheet';

const inputClass = "h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

const LOCATION_TYPES = ['studio', 'gym', 'client_home', 'outdoor', 'other'];

const LOCATION_TYPE_LABEL = {
  studio: 'Studio',
  gym: 'Gym',
  client_home: 'Client Home',
  outdoor: 'Outdoor',
  other: 'Other',
};

const LOCATION_TYPE_BADGE = {
  studio: 'bg-blue-100 text-blue-700',
  gym: 'bg-green-100 text-green-700',
  client_home: 'bg-purple-100 text-purple-700',
  outdoor: 'bg-orange-100 text-orange-700',
  other: 'bg-muted text-muted-foreground',
};

const TIMEZONES = [
  // North America
  'America/St_Johns',
  'America/Halifax',
  'America/Toronto',
  'America/New_York',
  'America/Chicago',
  'America/Winnipeg',
  'America/Regina',
  'America/Denver',
  'America/Edmonton',
  'America/Phoenix',
  'America/Vancouver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Honolulu',
  // Latin America
  'America/Mexico_City',
  'America/Bogota',
  'America/Lima',
  'America/Caracas',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  // Europe
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Vienna',
  'Europe/Budapest',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  // Africa
  'Africa/Lagos',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Africa/Johannesburg',
  // Middle East
  'Asia/Dubai',
  'Asia/Riyadh',
  // Asia
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Taipei',
  'Asia/Tokyo',
  'Asia/Seoul',
  // Australia & Pacific
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'Pacific/Fiji',
];

const EMPTY_FORM = { name: '', locationType: 'studio', address: '', timezone: 'America/Toronto' };

const Locations = () => {
  const { tenantDomain } = useParams();
  const { updateTenantDomain } = useAuth();

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    updateTenantDomain(tenantDomain);
    fetchLocations();
  }, [tenantDomain]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/location`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`${response.status}`);
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      setError('Failed to load locations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openCreateSheet = () => {
    setEditingLocation(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setSheetOpen(true);
  };

  const openEditSheet = (location) => {
    setEditingLocation(location);
    setForm({
      name: location.name,
      locationType: location.locationType,
      address: location.address ?? '',
      timezone: location.timezone,
      isActive: location.isActive,
    });
    setFormError(null);
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.locationType || !form.timezone.trim()) {
      setFormError('Name, type, and timezone are required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const isEdit = !!editingLocation;
      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/${tenantDomain}/location/${editingLocation.locationId}`
        : `${process.env.REACT_APP_API_URL}/${tenantDomain}/location`;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name.trim(),
          locationType: form.locationType,
          address: form.address.trim() || null,
          timezone: form.timezone.trim(),
          ...(isEdit && { isActive: form.isActive }),
        }),
      });
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchLocations();
      setSheetOpen(false);
    } catch (err) {
      setFormError('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${tenantDomain}/location/${deactivateTarget.locationId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!response.ok) throw new Error(`${response.status}`);
      await fetchLocations();
      setDeactivateTarget(null);
    } catch (err) {
      setError('Failed to deactivate location. Please try again.');
    } finally {
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="text-sm text-muted-foreground py-8">Loading...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Locations</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your training locations</p>
        </div>
        <Button onClick={openCreateSheet}>Add Location</Button>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {locations.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MapPin size={36} className="text-muted-foreground mb-3" />
          <p className="text-sm font-medium">No locations yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add your first training location to get started.</p>
          <Button className="mt-4" onClick={openCreateSheet}>Add Location</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map((loc) => (
            <div
              key={loc.locationId}
              className={`rounded-lg border bg-card p-5 flex flex-col gap-3 transition-opacity ${!loc.isActive ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="text-sm font-semibold truncate">{loc.name}</span>
                  <span className={`inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium ${LOCATION_TYPE_BADGE[loc.locationType]}`}>
                    {LOCATION_TYPE_LABEL[loc.locationType]}
                  </span>
                </div>
                {!loc.isActive && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground shrink-0">
                    Inactive
                  </span>
                )}
              </div>

              {loc.address && (
                <p className="text-xs text-muted-foreground">{loc.address}</p>
              )}
              <p className="text-xs text-muted-foreground">{loc.timezone}</p>

              <div className="flex gap-2 mt-auto pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => openEditSheet(loc)}>
                  <Pencil size={13} className="mr-1.5" /> Edit
                </Button>
                {loc.isActive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeactivateTarget(loc)}
                    className="border-destructive/40 text-destructive hover:bg-destructive/10"
                  >
                    <PowerOff size={13} className="mr-1.5" /> Deactivate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">{formError}</div>
              )}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Name <span className="text-destructive">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                  placeholder="e.g. King Street Studio"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Type <span className="text-destructive">*</span></label>
                <select
                  value={form.locationType}
                  onChange={(e) => setForm({ ...form, locationType: e.target.value })}
                  className={inputClass}
                >
                  {LOCATION_TYPES.map((t) => (
                    <option key={t} value={t}>{LOCATION_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Address</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={inputClass}
                  placeholder="123 Main St, Toronto, ON"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Timezone <span className="text-destructive">*</span></label>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  className={inputClass}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              {editingLocation && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is-active"
                    checked={form.isActive ?? true}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="h-4 w-4 rounded border-input"
                  />
                  <label htmlFor="is-active" className="text-sm font-medium">Active</label>
                </div>
              )}
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={!!deactivateTarget} onOpenChange={(open) => { if (!open) setDeactivateTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Location</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to deactivate <strong>{deactivateTarget?.name}</strong>? It will no longer be available for new bookings. Existing bookings will not be affected.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)} disabled={deactivating}>Cancel</Button>
            <Button
              onClick={handleDeactivate}
              disabled={deactivating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deactivating ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Locations;
